import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { ChevronLeft as CaretLeft, ChevronRight as CaretRight } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }

export default function CalendarPage() {
  const [posts, setPosts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [dragOver, setDragOver] = useState(null);

  const load = () => api.calendar().then(setPosts);
  useEffect(() => { load(); api.listBrands().then(setBrands); }, []);

  const onDragStart = (e, post) => {
    if (post.status === "published") {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("text/post_id", post.post_id);
    e.dataTransfer.setData("text/post_status", post.status || "");
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = async (e, date) => {
    e.preventDefault();
    setDragOver(null);
    const post_id = e.dataTransfer.getData("text/post_id");
    const post_status = e.dataTransfer.getData("text/post_status");
    if (!post_id || !date) return;
    // preserve time-of-day from original post
    const original = posts.find((p) => p.post_id === post_id);
    const oldKey = (original?.scheduled_date || "").slice(0, 10);
    const newKey = date.toISOString().slice(0, 10);
    if (oldKey === newKey) return; // no-op, user dropped on the same day
    const t = original?.scheduled_date ? new Date(original.scheduled_date) : new Date();
    const next = new Date(date);
    next.setHours(t.getHours(), t.getMinutes(), 0, 0);
    try {
      await api.editPost(post_id, { scheduled_date: next.toISOString() });
      toast.success(post_status === "approved"
        ? "Rescheduled — autopilot will ship at the new date."
        : "Rescheduled");
      load();
    } catch {
      toast.error("Reschedule failed");
    }
  };

  const days = useMemo(() => {
    const s = startOfMonth(cursor);
    const e = endOfMonth(cursor);
    const startWeekday = (s.getDay() + 6) % 7; // Monday-first
    const total = startWeekday + e.getDate();
    const cells = Math.ceil(total / 7) * 7;
    return Array.from({ length: cells }).map((_, i) => {
      const day = i - startWeekday + 1;
      if (day < 1 || day > e.getDate()) return null;
      return new Date(cursor.getFullYear(), cursor.getMonth(), day);
    });
  }, [cursor]);

  const byDate = useMemo(() => {
    const m = new Map();
    for (const p of posts) {
      // Backend supplies `calendar_date` = published_at for published posts, else scheduled_date.
      const key = (p.calendar_date || p.scheduled_date || "").slice(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(p);
    }
    return m;
  }, [posts]);

  return (
    <div data-testid="calendar-page" className="max-w-7xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-eyebrow mb-3">Year-Long Pipeline</div>
          <h1 className="font-display font-black text-5xl tracking-tighter">Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(addMonths(cursor, -1))} data-testid="prev-month-button" className="p-2 border border-black hover:bg-[#F4F5F6]"><CaretLeft size={16} /></button>
          <div className="px-4 py-2 border border-black font-display font-bold text-lg min-w-[170px] text-center">
            {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
          </div>
          <button onClick={() => setCursor(addMonths(cursor, 1))} data-testid="next-month-button" className="p-2 border border-black hover:bg-[#F4F5F6]"><CaretRight size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0 border-l border-t border-[#E2E4E9]">
        {DAYS.map((d) => (
          <div key={d} className="text-eyebrow p-3 border-r border-b border-[#E2E4E9] bg-[#F4F5F6]">{d}</div>
        ))}
        {days.map((d, i) => {
          const key = d?.toISOString().slice(0, 10);
          const list = (key && byDate.get(key)) || [];
          const over = dragOver === key;
          return (
            <div
              key={i}
              onDragOver={(e) => { if (d) { e.preventDefault(); setDragOver(key); } }}
              onDragLeave={() => setDragOver((v) => v === key ? null : v)}
              onDrop={(e) => d && onDrop(e, d)}
              className={`min-h-[120px] p-2 border-r border-b border-[#E2E4E9] ${!d ? "bg-[#FAFAFA]" : ""} ${over ? "bg-[#F4F5F6] outline outline-2 outline-[#002FA7]" : ""}`}
              data-testid={`day-cell-${key || i}`}
            >
              {d && (
                <>
                  <div className="text-xs mono text-[#A1A1AA] mb-2">{d.getDate()}</div>
                  <div className="space-y-1">
                    {list.slice(0, 3).map((p) => {
                      const b = brands.find((x) => x.brand_id === p.brand_id);
                      const color = p.status === "approved" ? "#059669" : p.status === "published" ? "#0A0A0B" : "#F59E0B";
                      const isDraggable = p.status !== "published";
                      return (
                        <div
                          key={p.post_id}
                          draggable={isDraggable}
                          onDragStart={(e) => onDragStart(e, p)}
                          className={isDraggable ? "cursor-grab active:cursor-grabbing" : ""}
                          data-testid={`draggable-post-${p.post_id}`}
                          title={isDraggable ? "Drag to a new day to reschedule" : "Published posts can't be rescheduled"}
                        >
                          <Link
                            to={`/approvals?post=${p.post_id}`}
                            draggable={false}
                            onDragStart={(e) => e.preventDefault()}
                            className="block px-2 py-1 border-l-4 hover:bg-[#F4F5F6] text-xs leading-tight"
                            style={{ borderColor: color }}
                            data-testid={`calendar-post-${p.post_id}`}
                          >
                            <div className="font-semibold line-clamp-1">{p.title}</div>
                            <div className="mono text-[10px] uppercase text-[#A1A1AA]">{b?.name || "—"}</div>
                          </Link>
                        </div>
                      );
                    })}
                    {list.length > 3 && <div className="text-xs text-[#A1A1AA] mono">+{list.length - 3} more</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex gap-6 text-xs mono text-[#52525B] items-center">
        <Legend color="#F59E0B" label="Pending" />
        <Legend color="#059669" label="Approved" />
        <Legend color="#0A0A0B" label="Published" />
        <span className="ml-auto text-[#A1A1AA]">Tip: drag pending or approved posts to a new day. Click any post to open it.</span>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3" style={{ background: color }} /> <span className="uppercase">{label}</span>
    </div>
  );
}
